import { useState, useCallback } from 'react'
import type {
  RelationshipImportValidationResult,
  RelationshipImportExecuteRequest,
  RelationshipImportExecuteResult,
} from '../types/relationship-import'
import {
  validateRelationshipImport,
  executeRelationshipImport,
} from '../services/relationship-import'

export type RelationshipImportPhase =
  | 'idle'
  | 'validating'
  | 'validated'
  | 'executing'
  | 'complete'
  | 'error'

export interface UseRelationshipImportResult {
  phase: RelationshipImportPhase
  loading: boolean
  error: string | null
  validationResult: RelationshipImportValidationResult | null
  importResult: RelationshipImportExecuteResult | null
  validate: (file: File) => Promise<RelationshipImportValidationResult | null>
  execute: () => Promise<RelationshipImportExecuteResult | null>
  reset: () => void
}

type StateSetter<T> = React.Dispatch<React.SetStateAction<T>>

interface ValidateArgs {
  caseId: string
  file: File
  setPhase: StateSetter<RelationshipImportPhase>
  setLoading: StateSetter<boolean>
  setError: StateSetter<string | null>
  setValidationResult: StateSetter<RelationshipImportValidationResult | null>
  setImportResult: StateSetter<RelationshipImportExecuteResult | null>
}

async function runValidation(
  args: ValidateArgs
): Promise<RelationshipImportValidationResult | null> {
  const { caseId, file, setPhase, setLoading, setError, setValidationResult, setImportResult } =
    args
  setPhase('validating')
  setLoading(true)
  setError(null)
  setValidationResult(null)
  setImportResult(null)
  try {
    const result = await validateRelationshipImport(caseId, file)
    setValidationResult(result)
    setPhase('validated')
    return result
  } catch (error_) {
    const message = error_ instanceof Error ? error_.message : 'Failed to validate file'
    setError(message)
    setPhase('error')
    return null
  } finally {
    setLoading(false)
  }
}

interface ExecuteArgs {
  caseId: string
  validationResult: RelationshipImportValidationResult
  setPhase: StateSetter<RelationshipImportPhase>
  setLoading: StateSetter<boolean>
  setError: StateSetter<string | null>
  setImportResult: StateSetter<RelationshipImportExecuteResult | null>
}

async function runExecution(args: ExecuteArgs): Promise<RelationshipImportExecuteResult | null> {
  const { caseId, validationResult, setPhase, setLoading, setError, setImportResult } = args
  setPhase('executing')
  setLoading(true)
  setError(null)
  try {
    const request: RelationshipImportExecuteRequest = {
      fileName: validationResult.fileName,
      records: validationResult.records,
      warnings: validationResult.warnings,
    }
    const result = await executeRelationshipImport(caseId, request)
    setImportResult(result)
    setPhase('complete')
    return result
  } catch (error_) {
    const message = error_ instanceof Error ? error_.message : 'Failed to execute import'
    setError(message)
    setPhase('error')
    return null
  } finally {
    setLoading(false)
  }
}

export function useRelationshipImport(caseId: string | null): UseRelationshipImportResult {
  const [phase, setPhase] = useState<RelationshipImportPhase>('idle')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [validationResult, setValidationResult] =
    useState<RelationshipImportValidationResult | null>(null)
  const [importResult, setImportResult] = useState<RelationshipImportExecuteResult | null>(null)

  const validate = useCallback(
    async (file: File): Promise<RelationshipImportValidationResult | null> => {
      if (!caseId) {
        setError('No case selected')
        return null
      }
      return runValidation({
        caseId,
        file,
        setPhase,
        setLoading,
        setError,
        setValidationResult,
        setImportResult,
      })
    },
    [caseId]
  )

  const execute = useCallback(async (): Promise<RelationshipImportExecuteResult | null> => {
    if (!caseId) {
      setError('No case selected')
      return null
    }
    if (!validationResult) {
      setError('No validated data available')
      return null
    }
    return runExecution({
      caseId,
      validationResult,
      setPhase,
      setLoading,
      setError,
      setImportResult,
    })
  }, [caseId, validationResult])

  const reset = useCallback((): void => {
    setPhase('idle')
    setLoading(false)
    setError(null)
    setValidationResult(null)
    setImportResult(null)
  }, [])

  return { phase, loading, error, validationResult, importResult, validate, execute, reset }
}
